import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Generic drag-and-drop / click-to-browse file picker. Deliberately framework-plain (no app-specific
 * coupling) so it can move into the shared onevedai-ui-angular library later once that package (or
 * this app) is on a compatible Angular version -- see feedback_schoolerp_generic_table.md's sibling
 * note in memory for the same "build here, promote later" call on this component.
 *
 * Only picks and validates files -- does not perform the upload itself. The consuming page owns the
 * actual HTTP call, since the upload endpoint/shape differs per feature.
 */
@Component({
  selector: 'app-file-upload',
  standalone: false,
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.css',
})
export class FileUploadComponent {
  /** Comma-separated extensions/mime types, e.g. ".pdf,.jpg,.png" -- same format as the native <input accept>. */
  @Input() accept = '*';
  @Input() multiple = true;
  @Input() maxSizeBytes = 10 * 1024 * 1024;
  @Input() label = 'Drag & drop files here, or click to browse';
  @Input() disabled = false;

  @Output() filesSelected = new EventEmitter<File[]>();

  isDragging = false;
  errorMessage = '';

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (this.disabled) return;
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    if (this.disabled || !event.dataTransfer?.files?.length) return;
    this.handleFiles(Array.from(event.dataTransfer.files));
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.handleFiles(Array.from(input.files));
    input.value = '';
  }

  private handleFiles(files: File[]): void {
    this.errorMessage = '';

    const selected = this.multiple ? files : files.slice(0, 1);

    const oversized = selected.find(f => f.size > this.maxSizeBytes);
    if (oversized) {
      this.errorMessage = `"${oversized.name}" exceeds the ${this.formatSize(this.maxSizeBytes)} size limit.`;
      return;
    }

    const acceptedExtensions = this.accept === '*'
      ? null
      : this.accept.split(',').map(a => a.trim().toLowerCase());

    if (acceptedExtensions) {
      const invalid = selected.find(f => {
        const ext = `.${f.name.split('.').pop()?.toLowerCase()}`;
        return !acceptedExtensions.includes(ext) && !acceptedExtensions.includes(f.type);
      });
      if (invalid) {
        this.errorMessage = `"${invalid.name}" is not an accepted file type (${this.accept}).`;
        return;
      }
    }

    this.filesSelected.emit(selected);
  }

  private formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
}
